import { create } from 'zustand';
import { getSite } from '../config/siteConfig';
import {
  getDefaultGradeThresholds,
  normalizeGradeThresholds,
  setLiveGradeThresholdsGetter,
} from '../Utils/gradePalette';

const LS_KEY_LEGACY = 'mdv-grade-palette';

function storageKey() {
  const name = String(getSite()?.projectName || '').trim();
  return name ? `${LS_KEY_LEGACY}:${name}` : LS_KEY_LEGACY;
}

function cloneDefaults() {
  return getDefaultGradeThresholds().map((row) => ({
    min: row.min,
    color: row.color,
    hidden: !!row.hidden,
  }));
}

function loadThresholds() {
  try {
    const raw = localStorage.getItem(storageKey()) || localStorage.getItem(LS_KEY_LEGACY);
    if (!raw) return cloneDefaults();
    const data = JSON.parse(raw);
    const list = Array.isArray(data) ? data : data?.thresholds;
    return normalizeGradeThresholds(list);
  } catch {
    return cloneDefaults();
  }
}

function persistThresholds(thresholds) {
  try {
    localStorage.setItem(storageKey(), JSON.stringify(normalizeGradeThresholds(thresholds)));
  } catch {
    // quota or private mode
  }
}

export const useGradePaletteStore = create((set, get) => ({
  thresholds: loadThresholds(),
  setThresholds: (next) => {
    const thresholds = normalizeGradeThresholds(next);
    persistThresholds(thresholds);
    set({ thresholds });
  },
  setBinMin: (index, min) => {
    const current = get().thresholds.map((row) => ({ ...row }));
    if (!current[index] || !Number.isFinite(current[index].min)) return;
    current[index].min = Number(min);
    get().setThresholds(current);
  },
  setBinColor: (index, color) => {
    const current = get().thresholds.map((row) => ({ ...row }));
    if (!current[index]) return;
    current[index].color = Number(color);
    get().setThresholds(current);
  },
  toggleBinHidden: (index) => {
    const current = get().thresholds.map((row) => ({ ...row }));
    if (!current[index]) return;
    current[index].hidden = !current[index].hidden;
    get().setThresholds(current);
  },
  addBin: () => {
    const current = get().thresholds.map((row) => ({ ...row }));
    const top = current.find((row) => Number.isFinite(row.min));
    const nextMin = Number.isFinite(top?.min) ? Number((top.min * 2 || 1).toFixed(2)) : 1;
    current.unshift({ min: nextMin, color: 0xffffff, hidden: false });
    get().setThresholds(current);
  },
  removeBin: (index) => {
    const current = get().thresholds.filter((_, i) => i !== index);
    const finite = current.filter((row) => Number.isFinite(row.min));
    if (!finite.length) return;
    get().setThresholds(current);
  },
  resetPalette: () => get().setThresholds(cloneDefaults()),
}));

setLiveGradeThresholdsGetter(() => useGradePaletteStore.getState().thresholds);

/** Call after loadSiteConfig() so first-load bins come from site.json, not the JS fallback. */
export function hydrateGradePaletteFromSite() {
  useGradePaletteStore.setState({ thresholds: loadThresholds() });
}
